import { cn } from '@seen/ui'
import {
  type JSX,
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  generateTimeOptions,
  getCustomTimeToInsert,
  getNextHighlightedValue,
  getScrollTargetValue,
  insertCustomTimeOption,
  isInvalidTimeInput,
  isTimeInRange,
  parseSearchToTime,
} from '#src/components/core/combobox/timeComboboxUtils.ts'
import { COMBOBOX_LIST_MAX_HEIGHT } from '#src/components/core/combobox/util.ts'
import { Popover } from '#src/components/core/popover/Popover.tsx'
import { PopoverContent } from '#src/components/core/popover/PopoverContent.tsx'
import { PopoverTrigger } from '#src/components/core/popover/PopoverTrigger.tsx'

export interface TimeComboboxProps {
  children: ReactNode
  onSelect: (value: string) => void
  /** Currently selected time in HH:mm format - used to scroll to on open */
  value?: string
  searchPlaceholder?: string
  className?: string
  disabled?: boolean
  /** Time increment in minutes (default: 15) */
  stepMinutes?: number
  /** Minimum time in HH:mm format, or null for no options (default: '00:00') */
  minTime?: string | null
  /** Maximum time in HH:mm format (default: '23:59') */
  maxTime?: string
}

/*
 * A time picker combobox optimized for appointment scheduling.
 *
 * Key behaviors:
 * - Shows all times in chronological order (midnight to midnight)
 * - Scrolls to the typed time instead of filtering
 * - Supports custom time entry (e.g., "3:10 PM")
 * - Does NOT use "recently used" sorting
 *
 * This is a custom implementation (not using cmdk) because the scroll-to-match
 * behavior conflicts with cmdk's typeahead/filter model.
 */
export function TimeCombobox({
  children,
  onSelect,
  value,
  searchPlaceholder = 'Type a time...',
  className,
  disabled = false,
  stepMinutes = 15,
  minTime = '00:00',
  maxTime = '23:59',
}: TimeComboboxProps): JSX.Element {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const [highlightedValue, setHighlightedValue] = useState<string | undefined>(
    undefined
  )
  const listRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const scrollBehaviorRef = useRef<'center' | 'nearest' | 'none'>('center')

  const baseTimeOptions = useMemo(
    () =>
      minTime === null
        ? []
        : generateTimeOptions(minTime, maxTime, stepMinutes),
    [minTime, maxTime, stepMinutes]
  )

  const parsedTime = useMemo(() => parseSearchToTime(search), [search])

  const isInvalidInput = useMemo(
    () => isInvalidTimeInput(search, parsedTime),
    [search, parsedTime]
  )

  const isOutOfRange = useMemo(
    () =>
      parsedTime !== null &&
      (minTime === null || !isTimeInRange(parsedTime, minTime, maxTime)),
    [parsedTime, minTime, maxTime]
  )

  const hasInputError = isInvalidInput || isOutOfRange

  const customTimeToInsert = useMemo(
    () =>
      getCustomTimeToInsert({
        search,
        parsedTime,
        value,
        baseOptions: baseTimeOptions,
        minTime,
        maxTime,
      }),
    [search, parsedTime, value, baseTimeOptions, minTime, maxTime]
  )

  const displayOptions = useMemo(
    () => insertCustomTimeOption(baseTimeOptions, customTimeToInsert),
    [baseTimeOptions, customTimeToInsert]
  )

  const scrollTargetValue = useMemo(
    () =>
      getScrollTargetValue({
        parsedTime,
        value,
        displayOptions,
        minTime,
        maxTime,
      }),
    [parsedTime, value, displayOptions, minTime, maxTime]
  )

  useEffect(() => {
    scrollBehaviorRef.current = 'center'
  }, [search])

  useEffect(() => {
    if (!open) {
      return
    }
    if (hasInputError) {
      setHighlightedValue(undefined)
      return
    }
    if (scrollTargetValue) {
      scrollBehaviorRef.current = 'center'
      setHighlightedValue(scrollTargetValue)
    }
  }, [open, scrollTargetValue, hasInputError])

  useEffect(() => {
    const scrollBehavior = scrollBehaviorRef.current
    if (open && highlightedValue && scrollBehavior !== 'none') {
      requestAnimationFrame(() => {
        const list = listRef.current
        if (!list) {
          return
        }
        const item = list.querySelector(
          `[data-value="${CSS.escape(highlightedValue)}"]`
        )
        if (item) {
          item.scrollIntoView({ block: scrollBehavior })
        }
      })
    }
  }, [open, highlightedValue])

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        inputRef.current?.focus()
      })
    }
  }, [open])

  const handleSelect = useCallback(
    (selectedValue: string): void => {
      setOpen(false)
      setSearch('')
      onSelect(selectedValue)
    },
    [onSelect]
  )

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent): void => {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        const next = getNextHighlightedValue(
          highlightedValue,
          displayOptions,
          'down'
        )
        if (next) {
          scrollBehaviorRef.current = 'nearest'
          setHighlightedValue(next)
        }
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        const prev = getNextHighlightedValue(
          highlightedValue,
          displayOptions,
          'up'
        )
        if (prev) {
          scrollBehaviorRef.current = 'nearest'
          setHighlightedValue(prev)
        }
      } else if (e.key === 'Enter') {
        e.preventDefault()
        if (highlightedValue) {
          handleSelect(highlightedValue)
        }
      }
    },
    [highlightedValue, displayOptions, handleSelect]
  )

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      setSearch(e.target.value)
    },
    []
  )

  const handleOpenChange = useCallback((newOpen: boolean) => {
    if (!newOpen) {
      setSearch('')
      setHighlightedValue(undefined)
    }
    setOpen(newOpen)
  }, [])

  return (
    <Popover open={!disabled && open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild disabled={disabled}>
        {children}
      </PopoverTrigger>
      <PopoverContent className={cn(className, 'p-0')} align="start">
        <div className="flex flex-col overflow-hidden rounded-lg">
          <input
            ref={inputRef}
            type="text"
            value={search}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder={searchPlaceholder}
            aria-activedescendant={
              highlightedValue ? `time-option-${highlightedValue}` : undefined
            }
            className={cn(
              'flex w-full border-zinc-200 border-b bg-transparent p-3 text-sm outline-hidden',
              'placeholder:text-placeholder',
              hasInputError && 'bg-red-50 text-red-900'
            )}
          />
          {hasInputError && (
            <div className="border-b bg-red-50 px-3 py-2 text-xs text-red-600">
              {isInvalidInput ? 'Invalid time' : 'Time outside allowed range'}
            </div>
          )}
          <div
            ref={listRef}
            role="listbox"
            className={cn('overflow-y-auto p-1', COMBOBOX_LIST_MAX_HEIGHT)}
          >
            {displayOptions.map((option) => (
              // oxlint-disable-next-line jsx-a11y/click-events-have-key-events -- keyboard events handled by input element which maintains focus
              <div
                key={option.value}
                id={`time-option-${option.value}`}
                role="option"
                tabIndex={-1}
                data-value={option.value}
                aria-selected={option.value === highlightedValue}
                onClick={() => handleSelect(option.value)}
                onPointerMove={() => {
                  scrollBehaviorRef.current = 'none'
                  setHighlightedValue(option.value)
                }}
                className={cn(
                  'relative flex cursor-default select-none items-center rounded-sm px-1.5 py-2 text-sm outline-hidden',
                  'justify-between gap-2',
                  option.value === highlightedValue &&
                    'bg-accent text-accent-foreground'
                )}
              >
                <span>{option.label}</span>
                {option.isCustom && (
                  <span className="text-xs text-muted-foreground">custom</span>
                )}
              </div>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
